import React, { useCallback, useMemo, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Stack } from '@mui/material'

import useSubscribeRefresh  from 'use-subscribe-actions'
import { TableContent, MainContent } from 'pages/PagesStyles'
import LoaderSitesMetrics from 'components/Cartography/LoaderMetrics'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'

import ExportButton from '../../Topbar/ExportButton'
import TextFilter from '../../Topbar/Filters/TextFilter'
import SitesHardwares, { SitesStatuses } from '../../Topbar/Filters/SitesFilter'
import TableSites from './Table'
import { normalizeString } from '../helpers'
import { useFilters, useResetFilters } from '../../store'
import useExport from './use-export'

const Sites = ({ sites }) => {
  const [filters, setFilters] = useFilters()
  const resetFilters = useResetFilters()
  const navigate = useNavigate()
  useSubscribeRefresh(() => {
    resetFilters()
    navigate('/monitoring/sites')
  })
  const convertHardwareType = (type) => {
    return type === 'ap' ? 'accesspoints' : type
  }
  const filterStatusFct = useCallback((site) => filters.status.includes(site.equipment.available_level), [filters.status])
  const filterHardwaresFct = useCallback((site) => filters.hardwares
    .map(type => `nb_${convertHardwareType(type)}_ok`)
    .some((key) => site.equipment[key]), [filters.hardwares])
  
  const filterVerbatimFct = useCallback((site) => {
    const normalizedSiteName = normalizeString(site.name)
    const normalizedSearch = normalizeString(filters.verbatim)
    return normalizedSiteName.includes(normalizedSearch)
  }, [filters.verbatim])
  const selectedSites = useMemo(() => {
    const filtersFct = [
      filters.status.length
        ? filterStatusFct
        : null,
      filters.hardwares.length
        ? filterHardwaresFct
        : null,
      filters.verbatim.length
        ? filterVerbatimFct
        : null
    ].filter(Boolean)
    if (filtersFct.length) {
      return sites.filter(site => filtersFct.every(fct => fct(site)))
    }
    return sites
  }, [sites, filters])
  const createExport = useExport({sites: selectedSites})
  useRegisterBreadcrumbAction(<ExportButton analyticsName="monitoring_sites" onClick={async () => { await createExport() }} />)

  useEffect(() => {
    resetFilters()
  }, [])

  return (
    <>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Stack direction={'row'} gap={1}>
          <SitesStatuses
            sites={selectedSites}
            onChange={(values) => {
              setFilters({ ...filters, status: values })
            }}
            values={filters.status}
          />
          <SitesHardwares
            sites={selectedSites}
            onChange={values => {
              setFilters({ ...filters, hardwares: values })
            }}
            values={filters.hardwares}
          />
          <TextFilter />
        </Stack>
      </Stack>
      <MainContent mode={'expand'}>
        <TableContent mode={'expand'}>
          <TableSites sites={selectedSites} />
        </TableContent>
        <Outlet context={{ sites: selectedSites }} />
      </MainContent>
    </>
  )
}

const Loader = () => {
  return (
    <Stack gap={1} sx={{ flex: 1, minHeight: 0 }}>
      <LoaderSitesMetrics loader={<TableContent mode={'expand'}><TableSkeleton /></TableContent>}>
        {({ sites }) => (
          <Sites sites={sites} />
        )}
      </LoaderSitesMetrics>
    </Stack>
  )
}

export default Loader
